import { Navigate, Outlet, type RouteObject } from "react-router-dom";

import { ProjectMutableGate } from "@/pages/Project/components/ProjectMutableGate";
import { CashFlowPage, FiscalDocuments, ProjectPayables, ProjectReceivables } from "@/router/lazyPages/finance";
import {
  ProjectBudgets,
  ProjectClientBudgets,
  ProjectCosts,
  ProjectDiaryEntries,
  ProjectDiaryEntryDetailPage,
  ProjectDocuments,
  ProjectEquipments,
  ProjectIndexRedirect,
  ProjectMaterials,
  ProjectOccurrences,
  ProjectOverview,
  ProjectPhotos,
  ProjectPurchaseOrders,
  ProjectPurchaseRequests,
  ProjectQuotations,
  PurchaseOrderDetailPage,
  PurchaseRequestDetailPage,
  ProjectReports,
  QuotationDetailPage,
  ProjectServiceFront,
  ProjectShell,
  ProjectTracking,
  Projects,
} from "@/router/lazyPages/project";
import { NotFound } from "@/router/lazyPages/shared";
import {
  DailyLogEditRedirect,
  DailyLogFormRedirect,
  ProjectBudgetCreateRedirect,
  ProjectBudgetEditRedirect,
  ProjectBudgetNewVersionRedirect,
  ProjectClientBudgetCreateRedirect,
  ProjectClientBudgetEditRedirect,
  ProjectClientBudgetNewVersionRedirect,
  ProjectDiaryEntryFormEditRedirect,
  ProjectDiaryEntryFormNewRedirect,
  ProjectDocumentCreateRedirect,
  ProjectDocumentEditRedirect,
  ProjectDocumentNewVersionRedirect,
  ProjectEditRedirect,
  ProjectEquipmentsAddRedirect,
  ProjectMaterialsAddRedirect,
  ProjectPayablesCreateRedirect,
  ProjectPayablesEditRedirect,
  ProjectReceivablesCreateRedirect,
  ProjectReceivablesEditRedirect,
  ProjectServiceFrontEditRedirect,
  PurchaseRequestEditRedirect,
  PurchaseRequestNewRedirect,
} from "./redirects";
import { skeletonRoute } from "@/router/skeletonRoute";
import { SkeletonVariant } from "@/types/routeSkeleton";

export const projectsListRoute: RouteObject = skeletonRoute({
  path: "/projects",
  permission: "nav.projects",
  skeleton: {
    skeletonVariant: SkeletonVariant.List,
    skeletonListShowPrimaryAction: true,
    skeletonListShowSearch: false,
  },
  element: <Projects />,
});

export const projectShellRoutes: RouteObject[] = [
  { index: true, element: <ProjectIndexRedirect /> },
  {
    path: "overview",
    handle: { permission: "project.overview.view" },
    element: <ProjectOverview />,
  },
  {
    path: "edit",
    handle: { permission: "projects.create" },
    element: <ProjectEditRedirect />,
  },
  {
    path: "tracking",
    handle: { permission: "project.tracking.view" },
    element: <ProjectTracking />,
  },
  {
    path: "project-service-fronts",
    handle: { permission: "project.projectServiceFront.view" },
    element: <ProjectServiceFront />,
  },
  {
    path: "project-service-fronts/new",
    handle: { permission: "project.projectServiceFront.write" },
    element: <Navigate to="../project-service-fronts" replace />,
  },
  {
    path: "project-service-fronts/:projectServiceFrontId/edit",
    handle: { permission: "project.projectServiceFront.write" },
    element: <ProjectServiceFrontEditRedirect />,
  },
  {
    path: "project-diary-entries",
    handle: { permission: "project.projectDiary.view" },
    element: <ProjectDiaryEntries />,
  },
  {
    path: "project-diary-entries/:projectDiaryEntryId",
    handle: { permission: "project.projectDiary.view" },
    element: <ProjectDiaryEntryDetailPage />,
  },
  {
    path: "daily-log",
    element: <Navigate to="project-diary-entries" replace />,
  },
  {
    path: "occurrences",
    handle: { permission: "project.occurrences.view" },
    element: <ProjectOccurrences />,
  },
  {
    path: "issues",
    element: <Navigate to="occurrences" replace />,
  },
  {
    path: "materials",
    handle: { permission: "project.materials.view" },
    element: <ProjectMaterials />,
  },
  {
    path: "materials/add",
    handle: { permission: "project.materials.write" },
    element: <ProjectMaterialsAddRedirect />,
  },
  {
    path: "equipments",
    handle: { permission: "project.equipments.view" },
    element: <ProjectEquipments />,
  },
  {
    path: "equipments/add",
    handle: { permission: "project.equipments.write" },
    element: <ProjectEquipmentsAddRedirect />,
  },
  {
    path: "client-budgets",
    handle: { permission: "project.clientBudgets.view" },
    element: <ProjectClientBudgets />,
  },
  {
    path: "client-budgets/new",
    handle: { permission: "project.clientBudgets.write" },
    element: <ProjectClientBudgetCreateRedirect />,
  },
  {
    path: "client-budgets/:clientBudgetId/edit",
    handle: { permission: "project.clientBudgets.write" },
    element: <ProjectClientBudgetEditRedirect />,
  },
  {
    path: "client-budgets/:clientBudgetId/new-version",
    handle: { permission: "project.clientBudgets.write" },
    element: <ProjectClientBudgetNewVersionRedirect />,
  },
  {
    path: "project-budgets",
    handle: { permission: "project.budgets.view" },
    element: <ProjectBudgets />,
  },
  {
    path: "project-budgets/new",
    handle: { permission: "project.budgets.write" },
    element: <ProjectBudgetCreateRedirect />,
  },
  {
    path: "project-budgets/:budgetId/edit",
    handle: { permission: "project.budgets.write" },
    element: <ProjectBudgetEditRedirect />,
  },
  {
    path: "project-budgets/:budgetId/new-version",
    handle: { permission: "project.budgets.write" },
    element: <ProjectBudgetNewVersionRedirect />,
  },
  skeletonRoute({
    path: "purchase-requests",
    permission: "project.purchaseRequests.view",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectPurchaseRequests />,
  }),
  {
    path: "purchase-requests/new",
    handle: { permission: "project.purchaseRequests.write" },
    element: <PurchaseRequestNewRedirect />,
  },
  {
    path: "purchase-requests/:purchaseRequestId/edit",
    handle: { permission: "project.purchaseRequests.write" },
    element: <PurchaseRequestEditRedirect />,
  },
  {
    path: "purchase-requests/:purchaseRequestId",
    handle: { permission: "project.purchaseRequests.view" },
    element: <PurchaseRequestDetailPage />,
  },
  skeletonRoute({
    path: "project-quotations",
    permission: "project.quotations.view",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectQuotations />,
  }),
  {
    path: "project-quotations/:quotationId",
    handle: { permission: "project.quotations.view" },
    element: <QuotationDetailPage />,
  },
  skeletonRoute({
    path: "purchase-orders",
    permission: "project.purchaseOrders.view",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectPurchaseOrders />,
  }),
  {
    path: "purchase-orders/:purchaseOrderId",
    handle: { permission: "project.purchaseOrders.view" },
    element: <PurchaseOrderDetailPage />,
  },
  {
    path: "costs",
    handle: { permission: "project.finance.view" },
    element: <ProjectCosts />,
  },
  skeletonRoute({
    path: "payables",
    permission: "finance.payables.view",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectPayables />,
  }),
  {
    path: "payables/new",
    handle: { permission: "finance.payables.write" },
    element: <ProjectPayablesCreateRedirect />,
  },
  {
    path: "payables/:id/edit",
    handle: { permission: "finance.payables.write" },
    element: <ProjectPayablesEditRedirect />,
  },
  skeletonRoute({
    path: "receivables",
    permission: "finance.receivables.view",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <ProjectReceivables />,
  }),
  {
    path: "receivables/new",
    handle: { permission: "finance.receivables.write" },
    element: <ProjectReceivablesCreateRedirect />,
  },
  {
    path: "receivables/:id/edit",
    handle: { permission: "finance.receivables.write" },
    element: <ProjectReceivablesEditRedirect />,
  },
  skeletonRoute({
    path: "invoices",
    permission: "finance.fiscal.view",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <FiscalDocuments />,
  }),
  skeletonRoute({
    path: "cash-flow",
    permission: "finance.cashFlow.view",
    skeleton: {
      skeletonVariant: SkeletonVariant.Embedded,
    },
    element: <CashFlowPage />,
  }),
  {
    path: "documents",
    handle: { permission: "project.documents.view" },
    element: <ProjectDocuments />,
  },
  {
    path: "documents/new",
    handle: { permission: "project.documents.write" },
    element: <ProjectDocumentCreateRedirect />,
  },
  {
    path: "documents/:documentId/edit",
    handle: { permission: "project.documents.write" },
    element: <ProjectDocumentEditRedirect />,
  },
  {
    path: "documents/:documentId/new-version",
    handle: { permission: "project.documents.write" },
    element: <ProjectDocumentNewVersionRedirect />,
  },
  {
    path: "photos",
    handle: { permission: "project.photos.view" },
    element: <ProjectPhotos />,
  },
  {
    path: "reports",
    handle: { permission: "project.projectReport.view" },
    element: <ProjectReports />,
  },
  { path: "settings", element: <Navigate to="overview" replace /> },
  { path: "*", element: <NotFound /> },
];

export const projectShellRoute: RouteObject = {
  path: "/projects/:projectId",
  handle: { sidebar: "project", permission: "nav.projects" },
  element: <ProjectShell />,
  children: projectShellRoutes,
};

export const projectMutableFormRoute: RouteObject = {
  element: (
    <ProjectMutableGate>
      <Outlet />
    </ProjectMutableGate>
  ),
  children: [
    skeletonRoute({
      path: "/projects/:projectId/project-diary-entries/new",
      permission: "project.projectDiary.write",
      skeleton: { skeletonVariant: SkeletonVariant.Form },
      element: <ProjectDiaryEntryFormNewRedirect />,
    }),
    skeletonRoute({
      path: "/projects/:projectId/project-diary-entries/:projectDiaryEntryId/edit",
      permission: "project.projectDiary.write",
      skeleton: { skeletonVariant: SkeletonVariant.Form },
      element: <ProjectDiaryEntryFormEditRedirect />,
    }),
    {
      path: "/projects/:projectId/daily-log/new",
      element: <DailyLogFormRedirect />,
    },
    {
      path: "/projects/:projectId/daily-log/:projectDiaryEntryId/edit",
      element: <DailyLogEditRedirect />,
    },
  ],
};

export const projectCreateRoute: RouteObject = {
  path: "/projects/new",
  element: <Navigate to="/projects?create=1" replace />,
};
